// market-and-patient-share.component.ts - po zmianach, test czy działa

import { Component, Input, OnInit } from '@angular/core';
import { TCenter, TCenterProjectsData } from '@shared/interfaces/TCenter';
import { TProject, TRegionBrick } from '@shared/interfaces/TProject';
import { computeMomentumLong, computeMomentumShort } from '@shared/utils/computeMomentum';
import { Observable } from 'rxjs';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { sumProperty } from 'src/app/helpers/sum-property.helper';
import { MarketAndPatientShare } from 'src/app/interfaces/market-and-patient-share.interface';
import { PatientsICD } from 'src/app/interfaces/patients-icd.interface';
import { Region } from 'src/app/interfaces/region.interface';
import { CategoriesService } from 'src/app/services/categories.service';
import { KamReportsService } from '../../services/kam-reports.service';

@Component({
  selector: 'app-market-and-patient-share',
  templateUrl: './market-and-patient-share.component.html',
  styleUrls: ['./market-and-patient-share.component.scss']
})
export class MarketAndPatientShareComponent implements OnInit {

  @Input() project: TProject;
  // Market and patient share tables data
  public marketAndPatientShare: MarketAndPatientShare;

  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;

  constructor(
    private _kamReportsService: KamReportsService,
    private _categoriesService: CategoriesService,
  ) {}

  // getters
  get centers$(): Observable<TCenter[]> { return this._kamReportsService.allCenters$ }
  get selectedCountries(): string[] { return this._kamReportsService.selectedCountries }
  get selectedRegions(): Region[] { return this._kamReportsService.selectedRegions }

  ngOnInit(): void {
    this.marketAndPatientShare = this.buildMarketAndPatientShare(this.project);
    // console.log('M&P PROJECT', this.project);
  }

  /**
   * build data for Market and patient share table
   * @param  {TProject} project
   * @param  {TCenter[]} centers
   * @returns Observable
   */
  buildMarketAndPatientShare(project: TProject): MarketAndPatientShare {
    const centers = this._kamReportsService.allCenters;
    const filteredBricks: TRegionBrick[] = this._kamReportsService.filterBricksBySelectedRegions(project?.accountBricks, this.selectedRegions);
    // console.log('M&P SHARE bricks & Filtered', project?.accountBricks, filteredBricks);
    // const momentum$: Observable<number[]> = this._momentumService.computeMomentumRequest$(this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'patients')); // momentum
    const momentumData: number[] = this._kamReportsService.getLastXMonthsTotal(filteredBricks, 16, 'patients'); // get last 16 months data for patients
    // console.log('M&P SHARE momentumData', momentumData);
    const momentumShort = Math.round(computeMomentumShort(momentumData));
    const momentumLong = Math.round(computeMomentumLong(momentumData));

    // transform responses

    // get all project centers from selected countries
    const patientsIcd: PatientsICD[] = centers.map(center => {
      const projectData: TCenterProjectsData = center.projectsData.filter(item => item.projectId === this._categoriesService.getSelectedProject()?.id)[0];
      const result: PatientsICD = {
        centerId: center.c_id,
        country: center.c_country,
        region: projectData.c_project_account_region,
        potential: projectData.c_proj_patient_potential2,
      };
      return result;
    });

    // filter PatientsICDs by selected regions
    const regionalPatientsIcd: PatientsICD[] = patientsIcd.filter(item => {
      if (this.selectedRegions.length) {
        return this.selectedRegions.map(region => region.id).includes(item.region)
      } else {
        return true;
      }
    });

    const dsTotal = this._kamReportsService.getTotalFromClosestMonth(filteredBricks, 'patients');
    const dsFourMonth = momentumShort;
    const dsPy = momentumLong;
    const pIcdTotalNational = sumProperty(patientsIcd, 'potential');
    const pIcdTotalTerritory = sumProperty(regionalPatientsIcd, 'potential');
    const uppTotalNational = Math.round(this._kamReportsService.sumAllBrickSales(project.accountBricks) / pIcdTotalNational); // TODO change to bricks brom last months?
    const uppTotalTerritory = Math.round(this._kamReportsService.sumAllBrickSales(project.accountBricks, this.selectedRegions) / pIcdTotalTerritory); // TODO change to bricks brom last months?

    const competitors: { name: string, total: number, fourMonth: number, py: number }[] = [];
    // Add competitors with sum of patients and momentums for all bricks in region and in country (so from all)
    // console.log('M&P SHARE Filtered bricks', filteredBricks);
    // console.log('PROJECT COMPETITORS', project.competitors);
    // console.log('FILTERED BRICKS', filteredBricks);
    if (!project.competitors) project.competitors = [];
    project.competitors.forEach(competitor => {
      // console.log('competitor', competitor);
      competitors.push({
        name: competitor.name,
        total: this._kamReportsService.getCompetitorsTotalFromClosestMonth(competitor.id, filteredBricks, 'patients'),
        // fourMonth: Math.round(computeMomentumShort(this._kamReportsService.getLastXMonthsCompetitorsTotal(competitor.id, filteredBricks, 16, 'patients'))),
        // py: Math.round(computeMomentumLong(this._kamReportsService.getLastXMonthsCompetitorsTotal(competitor.id, filteredBricks, 16, 'patients'))),
        fourMonth: this.getPatientsMomentumShortTotal(competitor.id, filteredBricks),
        py: this.getPatientsMomentumLongTotal(competitor.id, filteredBricks),
      });
    });

    // create result object
    const result: MarketAndPatientShare = {
      directSales: { total: dsTotal, fourMonth: dsFourMonth, py: dsPy },
      patientsIcd: { totalNational: pIcdTotalNational, totalTerritory: pIcdTotalTerritory },
      utilisationPerPatient: { totalNational: uppTotalNational, totalTerritory: uppTotalTerritory },
      competitors,
    };

    // console.log('M&P SHARE result', result);
    return result;
  }


  getPatientsMomentumShortTotal(competitorId: string, bricks: TRegionBrick[]): number {
    const momentunData = this._kamReportsService.getLastXMonthsCompetitorsTotal(competitorId, bricks, 16, 'patients');
    return Math.round(computeMomentumShort(momentunData));
  }

  getPatientsMomentumLongTotal(competitorId: string, bricks: TRegionBrick[]): number {
    const momentunData = this._kamReportsService.getLastXMonthsCompetitorsTotal(competitorId, bricks, 16, 'patients');
    return Math.round(computeMomentumLong(momentunData));
  }

  // calculateMomentumShort(sales: TBrickSale[], target: keyof TBrickSale): number {
  //   const items = this.sortCompetitorsSalesChronologically(sales).map((s) => Number(s[target]));
  //   if (items.length < 8) return 0;
  //   return computeMomentumShort(items);
  // }

  // calculateMomentumLong(sales: TBrickSale[], target: keyof TBrickSale): number {
  //   const items = this.sortCompetitorsSalesChronologically(sales).map((s) => Number(s[target]));
  //   if (items.length < 8) return 0;
  //   return computeMomentumLong(items);
  // }

  // private sortCompetitorsSalesChronologically(data: TBrickSale[]): TBrickSale[] {
  //   return [...data].sort((a, b) => {
  //     const yearA = Number(a.year);
  //     const yearB = Number(b.year);
  //     const monthA = Number(a.month);
  //     const monthB = Number(b.month);
  //     if (yearA !== yearB) return yearA - yearB;
  //     return monthA - monthB;
  //   });
  // }

  getProjectName(): string {
    const project = this._categoriesService.getSelectedProject();
    return project ? project.name : 'Project not found';
  }
}
